import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  SelectChangeEvent,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TEXT_COLORS, BORDER_COLORS } from '../utils/colors';
import { l2Api } from '../api/l2.service';
import type { GameServerDto } from '../types';

const ServerSelection: React.FC = () => {
  const { t } = useTranslation();
  const selectRef = useRef<HTMLDivElement>(null);
  const [servers, setServers] = useState<GameServerDto[]>([]);
  const [selectedServerId, setSelectedServerId] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadServers = async () => {
      try {
        const data = await l2Api.getServers();
        setServers(data);

        // Restore selected server from localStorage or select first
        const storedServerId = localStorage.getItem('selectedServerId');
        if (storedServerId && data.some(s => s.id.toString() === storedServerId)) {
          setSelectedServerId(storedServerId);
        } else if (data.length > 0) {
          const firstServerId = data[0].id.toString();
          setSelectedServerId(firstServerId);
          localStorage.setItem('selectedServerId', firstServerId);
        }
      } catch (error) {
        console.error('Failed to load servers:', error);
      } finally {
        setLoading(false);
      }
    };

    loadServers();
  }, []);

  const handleServerChange = (event: SelectChangeEvent<string>) => {
    const newServerId = event.target.value;
    setSelectedServerId(newServerId);
    localStorage.setItem('selectedServerId', newServerId);

    // Reload page to apply new server context
    window.location.reload();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} sx={{ color: 'white' }} />
      </Box>
    );
  }

  if (servers.length === 0) {
    return null;
  }

  return (
    <>
      <FormControl
        fullWidth
        sx={{
          '& .MuiOutlinedInput-root': {
            color: 'white',
            backgroundColor: 'transparent',
            '& fieldset': {
              borderColor: BORDER_COLORS.FOCUS,
            },
            '&:hover fieldset': {
              borderColor: BORDER_COLORS.FOCUS,
            },
            '&.Mui-focused fieldset': {
              borderColor: 'white',
            },
          },
          '& .MuiSelect-icon': {
            color: 'white',
          },
        }}
      >
        <Select
          value={selectedServerId}
          ref={selectRef}
          size="small"
          onChange={handleServerChange}
          onClose={() => {
            setTimeout(() => {
              if (selectRef.current) {
                selectRef.current.blur();
              }
            }, 100);
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                backdropFilter: 'blur(12px)',
                border: `1px solid ${BORDER_COLORS.LIGHTER}`,
              },
            },
          }}
        >
          {servers.map((server) => (
            <MenuItem key={server.id} value={server.id.toString()}>
              {server.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box sx={{ pl: '5px', mt: '7px' }}>
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: '13px',
            color: TEXT_COLORS.DISABLED,
            letterSpacing: '0.3px',
            textTransform: 'capitalize',
            lineHeight: '21px',
          }}
        >
          {t('navigation:toolbar.switchServer')}
        </Typography>
      </Box>
    </>
  );
};

export default ServerSelection;
